import { useMemo } from 'react';
import { useMatches, useParams } from 'react-router';
import type { Organization } from '~/types';

export interface OrganizationHandle {
  organization: (match: any) => Organization;
}

export interface RootHandle {
  root: (match: any) => {
    orgSlug: string | undefined;
    hasCustomDomain: boolean;
  };
}

function isOrganizationHandle(handle: unknown): handle is OrganizationHandle {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'organization' in handle &&
    typeof (handle as OrganizationHandle).organization === 'function'
  );
}

export function useOrganization(): {
  orgSlug: string;
  organization?: Organization;
  hasCustomDomain: boolean;
} {
  const matches = useMatches();
  const params = useParams();

  return useMemo(() => {
    const rootMatch = matches[0];
    const rootHandle = rootMatch?.handle as RootHandle | undefined;

    if (!rootHandle) {
      throw new Error('Root handle not found');
    }

    const { orgSlug: rootOrgSlug, hasCustomDomain } =
      rootHandle.root(rootMatch);

    const matchOrganization = matches.find((m) =>
      isOrganizationHandle(m.handle),
    );

    let result: ReturnType<typeof useOrganization> | undefined;

    if (params.orgSlug) {
      result = {
        orgSlug: params.orgSlug,
        organization: undefined,
        hasCustomDomain,
      };
    }

    if (rootOrgSlug) {
      result = {
        orgSlug: rootOrgSlug,
        organization: undefined,
        hasCustomDomain,
      };
    }

    if (matchOrganization && isOrganizationHandle(matchOrganization.handle)) {
      const organization =
        matchOrganization.handle.organization(matchOrganization);

      result = {
        orgSlug: organization.slug,
        organization,
        hasCustomDomain,
      };
    }

    if (result) {
      return result;
    }

    throw new Error('Organization not found');
  }, [matches, params]);
}
